import { SOUND_CATEGORIES, type SoundCategory } from "@yjsgames/sound";
import type { TargetFpsSetting } from "@yjsgames/perf";
import { DEFAULT_LANGUAGE, type LanguageCode } from "../i18n/locales.js";
import { DEFAULT_NUMBER_FORMAT, type NumberFormat } from "./ModuleBrickSaveData.js";
import { FORCE_PERF_OVERLAY_ENABLED } from "../env/appFlavor.js";

/**
 * 볼륨 슬라이더 최대 레벨 (0~MAX). `level/MAX_VOLUME_LEVEL`이 그룹 볼륨(0~1).
 * `SegmentLevelBar` maxLevel + `SoundController.setCategoryLevel`과 정합하는 SSoT.
 */
export const MAX_VOLUME_LEVEL = 10;

/** 신규/미설정 시 카테고리 볼륨 기본 레벨 (0~MAX_VOLUME_LEVEL 중). */
export const DEFAULT_SOUND_VOLUME_LEVEL = 7;

/** Perf overlay는 진단 빌드 플래그가 켜진 APK에서만 활성화한다. */
export const PERF_OVERLAY_AVAILABLE = FORCE_PERF_OVERLAY_ENABLED;
/** 진단 빌드에서는 저장 설정과 무관하게 overlay를 켠다. */
export const PERF_OVERLAY_FORCED = FORCE_PERF_OVERLAY_ENABLED;

/**
 * 기기별 설정 — 진행 데이터(ModuleBrickSaveData)와 분리해 **로컬 전용**으로 저장한다(BrickSettingsStore).
 * 클라우드 동기화 대상이 아니다(기기마다 볼륨/언어/표기형식이 독립). idleblade `SettingDTO`(SaveLocation.Local) 대응.
 */
export interface BrickSettings {
  /** 사운드 카테고리(bgm/sfx/ui) 볼륨 레벨 (0~MAX_VOLUME_LEVEL). */
  soundVolumes: Record<SoundCategory, number>;
  /** UI 표시 언어. */
  language: LanguageCode;
  /** 큰 숫자 표기 형식. */
  numberFormat: NumberFormat;
  /** 목표 프레임레이트 — "auto"=실측 적응(고주사율 기기 발열 완화), 15/30/60/120=고정 캡. */
  targetFps: TargetFpsSetting;
  /**
   * 성능 오버레이(perf overlay) 표시 여부.
   * 일반 빌드는 비활성이다. 진단 빌드(`VITE_FORCE_PERF_OVERLAY=true`)에서는 저장값과 무관하게 true.
   */
  perfOverlayEnabled: boolean;
}

/** 기본 설정 — soundVolumes는 전 카테고리 DEFAULT_SOUND_VOLUME_LEVEL. language는 마이그레이션/부팅에서 보정. */
export function createDefaultBrickSettings(): BrickSettings {
  return {
    soundVolumes: Object.fromEntries(
      SOUND_CATEGORIES.map((c) => [c, DEFAULT_SOUND_VOLUME_LEVEL]),
    ) as Record<SoundCategory, number>,
    language: DEFAULT_LANGUAGE,
    numberFormat: DEFAULT_NUMBER_FORMAT,
    targetFps: "auto",
    perfOverlayEnabled: PERF_OVERLAY_FORCED,
  };
}
